import { ListSystemNodesBySystemNodeIdsForPreviewQuery } from '@/graphql/codegen/graphql';
import { ListSystemNodesBySystemNodeIdsForPreview } from '@/graphql/queries/ListSystemNodesBySystemNodeIdsForPreview.gql';
import { UseQueryResult } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useFetchAndManageSystemNodes } from './useFetchAndManageSystemNodes';

type SystemNode = {
  __typename: 'FileNode';
  id?: string | null;
  kind?: string | null;
  name?: string | null;
  parentNodeId?: string | null;
  fileId?: string | null;
  file?:
    | {
        __typename?: 'AssetFile';
        signedGetObjectUrl?: string | null;
        signedGetObjectDownloadUrl?: string | null;
        storage?: {
          __typename?: 'FileStorageField';
          name?: string | null;
          size?: number | null;
        } | null;
      }
    | {
        __typename?: 'ImageFile';
        signedGetObjectUrl?: string | null;
        signedGetObjectDownloadUrl?: string | null;
        storage?: {
          __typename?: 'FileStorageField';
          name?: string | null;
          size?: number | null;
        } | null;
      }
    | {
        __typename?: 'ObjFile';
        signedGetObjectUrl?: string | null;
        signedGetObjectDownloadUrl?: string | null;
        storage?: {
          __typename?: 'FileStorageField';
          name?: string | null;
          size?: number | null;
        } | null;
      }
    | {
        __typename?: 'Ortho2dFile';
        signedGetObjectUrl?: string | null;
        signedGetObjectDownloadUrl?: string | null;
        storage?: {
          __typename?: 'FileStorageField';
          name?: string | null;
          size?: number | null;
        } | null;
      }
    | {
        __typename?: 'Tileset3dFile';
        signedGetObjectUrl?: string | null;
        signedGetObjectDownloadUrl?: string | null;
        storage?: {
          __typename?: 'FileStorageField';
          name?: string | null;
          size?: number | null;
        } | null;
      }
    | null;
};

export const useSystemNodesBySystemNodeIdsForPreviewWithSelector = <T>(
  systemNodeId: string,
  selector: (systemNode: undefined | SystemNode) => T,
) => {
  const { displaySystemNodes } = useFetchAndManageSystemNodes();
  const systemNodeIds = displaySystemNodes
    .reduce<string[]>((prev, curr) => {
      if (curr.fileId) return [...prev, curr.id];
      return prev;
    }, [])
    .sort();

  const select = useCallback(
    (data: ListSystemNodesBySystemNodeIdsForPreviewQuery) => {
      const systemNodes = data.findSystemNodesByIds ?? [];
      const systemNode = systemNodes.find(
        node => node && node.__typename === 'FileNode' && node.id === systemNodeId,
      ) as undefined | SystemNode;
      return selector(systemNode);
    },
    [selector, systemNodeId],
  );

  const response = ListSystemNodesBySystemNodeIdsForPreview.useQuery(
    { systemNodeIds },
    {
      enabled: systemNodeIds.length > 0,
      // TODO: graphql-tools does not support selector
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      select,
    },
  );

  return response as UseQueryResult<T>;
};
